import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useTranslation } from "../../context/TranslationsProvider";
import artworksData from "../../data/artworks.json";
import Header from "./components/Header";
import Footer from "./components/Footer";
import axios from "axios";
import {Swiper, SwiperSlide} from "swiper/react";
import {Navigation, Pagination, Thumbs} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface GalleryImage {
    index: number;
    url: string;
    file?: File;
}

interface ProductState {
    id: number;
    title_lv: string;
    title_eng: string;
    title_ru: string;
    description_lv: string;
    description_eng: string;
    description_ru: string;
    size: string;
    price: string;
    main_image: string | undefined
    gallery_images: (GalleryImage | null)[];
    is_sold: boolean;
}

const SinglePainting: React.FC = () => {

    const { addToCart, cart } = useCart();
    const { translate, language } = useTranslation();
    const navigate = useNavigate();
    const [showPopup, setShowPopup] = useState(false);

    const [loader, setLoader] = useState(true);

    const [thumbsSwiper, setThumbsSwiper] = useState(null);


    const API_URL = `${process.env.REACT_APP_API_URL}`

    const params = useParams();

    const [product, setProduct] = useState<ProductState>({
        id: 0,
        title_lv: '',
        title_eng: '',
        title_ru: '',
        description_lv: '',
        description_eng: '',
        description_ru: '',
        size: '',
        price: '',
        main_image: undefined,
        gallery_images: Array<GalleryImage | null>(6).fill(null),
        is_sold: false
    });

    const fetchSingleProduct = () => {
        axios.get(`${API_URL}/single-product/${params.title}`)
            .then(response => {
                const responseData = response.data
                setProduct({
                    id: responseData.id,
                    title_lv: responseData.product_title_lv,
                    title_eng: responseData.product_title_eng,
                    title_ru: responseData.product_title_ru,
                    description_lv: responseData.product_desc_lv,
                    description_eng: responseData.product_desc_eng,
                    description_ru: responseData.product_desc_ru,
                    size: responseData.dimensions,
                    price: responseData.price,
                    main_image: responseData.main_image,
                    gallery_images: responseData.gallery_images
                        ? [
                            ...responseData.gallery_images,
                            ...Array(6 - responseData.gallery_images.length).fill(null)
                        ]
                        : Array(6).fill(null),
                    is_sold: responseData.is_sold
                })
                setLoader(false);

            })
            .catch(errors => {
                setLoader(false)
            })
    }

    useEffect(() => {
        fetchSingleProduct()
    }, [])

    const productTitle = params.name

    const isInCart = cart.some(
        (item) => item.title.toLowerCase() === productTitle?.toLowerCase()
    );

    const handleAddToCart = () => {
        if (!isInCart && product) {
            addToCart({
                id: product.id,
                title: product.title_eng,
                image: product.main_image,
                price: parseFloat(product.price),
                dimensions: product.size || "Unknown",
            });
            setShowPopup(true);
        }
    };


    return (
        <div className="flex flex-col bg-[#f4f1ea]">
            <Header />
            <div className="flex flex-col items-center min-h-screen p-2 md:p-5 lg:p-10">
                    <div className="container max-w-6xl flex flex-col flex-grow p-2 lg:flex-row gap-6 lg:gap-10 shadow-lg lg:p-8 bg-white">
                        {loader ? (
                            <div className="animate-pulse flex flex-col lg:flex-row flex-grow space-y-4 lg:space-y-0 lg:space-x-4">
                                <div className="w-full lg:w-1/2 flex-grow flex flex-col">
                                    <div className="h-96 lg:flex-grow bg-zinc-300"></div>
                                    <div className="flex space-x-4 mt-2 h-20 lg:h-32">
                                        <div className="bg-zinc-300 mt-2 w-full"></div>
                                        <div className="bg-zinc-300 mt-2 w-full"></div>
                                        <div className="bg-zinc-300 mt-2 w-full"></div>
                                        <div className="bg-zinc-300 mt-2 w-full"></div>
                                    </div>
                                </div>

                                <div className="w-full flex flex-col lg:w-1/2 space-y-4">
                                    <div className="h-24 lg:h-32 bg-zinc-300 w-full"></div>
                                    <div className="h-12 bg-zinc-300 w-1/3"></div>
                                    <div className="h-12 bg-zinc-300 w-1/2"></div>
                                    <div className="h-16 bg-zinc-300 w-full"></div>
                                    <div className="h-64 lg:flex-grow bg-zinc-300 w-full"></div>
                                </div>
                            </div>
                        ):(
                            <>
                            <div className="flex-1 flex flex-col items-center justify-center">
                                <Swiper
                                    pagination={{ clickable: true }}
                                    modules={[Thumbs]}
                                    className="w-full lg:w-[30rem]"
                                    thumbs={{ swiper: thumbsSwiper }}

                                >
                                    {product.main_image && (
                                        <SwiperSlide>
                                            <img
                                                src={product.main_image}
                                                alt={product.title_eng}
                                                className="object-contain"
                                            />
                                        </SwiperSlide>
                                    )}

                                    {product.gallery_images
                                        .filter(img => img !== null)
                                        .map((img, index) => (
                                            <SwiperSlide key={index}>
                                                <img
                                                    src={img?.url || ""}
                                                    alt={`Gallery Image ${index + 1}`}
                                                    className="object-contain"
                                                />
                                            </SwiperSlide>
                                        ))}
                                </Swiper>
                                <Swiper
                                    onSwiper={setThumbsSwiper}
                                    spaceBetween={8}
                                    slidesPerView={4}
                                    freeMode={true}
                                    watchSlidesProgress={true}
                                    modules={[Thumbs]}
                                    className="w-full lg:w-[30rem] h-[5rem] mt-2"
                                >
                                    {product.main_image && (
                                        <SwiperSlide>
                                            <img
                                                src={product.main_image}
                                                alt={product[`title_${language}` as keyof ProductState] as string}
                                                className="object-cover cursor-pointer"
                                            />
                                        </SwiperSlide>
                                    )}

                                    {product.gallery_images
                                        .filter(img => img !== null)
                                        .map((img, index) => (
                                            <SwiperSlide key={index}>
                                                <img
                                                    src={img?.url || ""}
                                                    alt={`Gallery Image ${index + 1}`}
                                                    className="object-cover cursor-pointer"
                                                />
                                            </SwiperSlide>
                                        ))}
                                </Swiper>
                            </div>
                        <div className="flex-1 flex flex-col mt-6 lg:mt-10">
                            <h1 className="text-4xl sm:text-5xl font-bold">{product[`title_${language}` as keyof ProductState] as string}</h1>
                            <p className="text-xl sm:text-2xl font-semibold mt-3 sm:mt-4">
                                {product.is_sold ? product.price + " €" : translate("sold")}
                            </p>
                            <p className="text-md sm:text-lg text-gray-600 mt-2">
                                {translate("product_dimensions_title")}: {product.size} (cm)
                            </p>
                            {product.is_sold ? (
                                <button
                                    className={`mt-5 sm:mt-6 px-5 sm:px-6 py-3 rounded-md transition ${
                                        isInCart
                                            ? "bg-gray-800 text-orange-100"
                                            : "bg-black text-orange-100 hover:bg-gray-800"
                                    }`}
                                    disabled={isInCart}
                                    onClick={handleAddToCart}
                                >
                                    {isInCart ? translate("cart_already_in_cart") : translate("cart_add_button")}
                                </button>
                            ):(
                                <button
                                    className={`mt-5 sm:mt-6 px-5 sm:px-6 py-3 rounded-md bg-black opacity-75 text-orange-100 cursor-not-allowed`}
                                    disabled={true}
                                >
                                    {translate("sold")}
                                </button>
                            )}

                            <div className="mt-8 sm:mt-10">
                                <h2 className="text-lg sm:text-xl font-semibold border-b-2 pb-2">{translate("product_description_title")}</h2>
                                <p className="mt-2 text-gray-700 text-sm sm:text-base">{product[`description_${language}` as keyof ProductState] as string}</p>
                            </div>
                        </div>
                        </>
                    )}
                </div>
            </div>
            <Footer />

            {showPopup && (
                <div className="fixed top-20 left-1/2 z-50 transform -translate-x-1/2 bg-white text-black px-6 pt-2 pb-6 shadow-lg flex flex-col items-center w-80 sm:w-96 lg:w-[400px]">
                    <button onClick={() => setShowPopup(false)} className="self-end text-gray-700 hover:text-gray-900 text-xl">
                        &times;
                    </button>

                    <div className="w-14 sm:w-16 h-14 sm:h-16 flex items-center justify-center bg-black rounded-full">
                        <img src="/assets/icons/cart.svg" alt="Cart" className="w-8 h-8 sm:w-12 sm:h-12" />
                    </div>

                    <span className="mb-4 text-lg text-center px-2">
                        {translate("cart_added_message")}
                    </span>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 w-full justify-center items-center">
                        <button onClick={() => navigate(`/cart`)} className="bg-black text-orange-100 px-4 py-2 rounded-md hover:bg-gray-800 w-full sm:w-auto text-center">
                            {translate("cart_goto")}
                        </button>
                        <button onClick={() => setShowPopup(false)} className="bg-black text-orange-100 px-4 py-2 rounded-md hover:bg-gray-800 w-full sm:w-auto text-center">
                            {translate("continue_shopping")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SinglePainting;
